import React, { FC, useEffect, useMemo, useState } from 'react';
import { Skeleton } from 'antd';
import { DashboardCard } from 'common/components/DashboardCard';
import { useAppSelector } from 'app/store/store.hooks';
import { ITrackStoreStore } from 'entities/TrackStore/TrackStore.models';
import { TrackStoreStoreCard } from 'entities/TrackStore/components/TrackStoreStoreCard';
import {
  getTrackStoreLiveStoreMap,
  getTrackStoreOnDutyMap,
  getTrackStorePinnedStoreCodes,
  getTrackStoreStoreCards,
  getTrackStoreStoreCardsSortedByPin,
  setTrackStorePinnedStoreCodes,
} from 'entities/TrackStore/TrackStore.helper';

interface IComponentProps {
  store?: ITrackStoreStore;
}

export const TrackStoreStoreCards: FC<IComponentProps> = ({ store }) => {
  const {
    trackStoreAllStoresCollection,
    trackStoreAllStoresCollectionLoading,
    trackStoreOnDutyCollection,
    trackStoreOnDutyCollectionLoading,
    trackStoreLiveStoresCollection,
    trackStoreLiveStoresCollectionLoading,
  } = useAppSelector((state) => state.trackStore);
  const authUserId = useAppSelector((state) => state.auth.authUser?.id);
  const [pinnedStoreCodes, setPinnedStoreCodes] = useState<string[]>([]);
  const storeCards = useMemo(
    () => getTrackStoreStoreCards(trackStoreAllStoresCollection, store),
    [trackStoreAllStoresCollection?.data, store]
  );
  const sortedStoreCards = useMemo(
    () => getTrackStoreStoreCardsSortedByPin(storeCards, pinnedStoreCodes),
    [storeCards, pinnedStoreCodes]
  );
  const onDutyMap = useMemo(() => getTrackStoreOnDutyMap(trackStoreOnDutyCollection), [trackStoreOnDutyCollection]);
  const liveStoresMap = useMemo(
    () => getTrackStoreLiveStoreMap(trackStoreLiveStoresCollection),
    [trackStoreLiveStoresCollection]
  );

  useEffect(() => {
    setPinnedStoreCodes(getTrackStorePinnedStoreCodes(authUserId));
  }, [authUserId]);

  const togglePin = (storeCode: string): void => {
    const nextPinnedStoreCodes = pinnedStoreCodes.includes(storeCode)
      ? pinnedStoreCodes.filter((item) => item !== storeCode)
      : [storeCode, ...pinnedStoreCodes];

    setPinnedStoreCodes(nextPinnedStoreCodes);

    if (authUserId) {
      setTrackStorePinnedStoreCodes(authUserId, nextPinnedStoreCodes);
    }
  };

  const loading =
    trackStoreAllStoresCollectionLoading || trackStoreOnDutyCollectionLoading || trackStoreLiveStoresCollectionLoading;

  return (
    <div className="mb-5 relative flex__item_1">
      <div className="pl-7 pr-7 scrollbar-x tsrt-store-cards-scroll">
        {loading ? (
          <>
            <DashboardCard title="" variant="store">
              <div className="p-5">
                <Skeleton active paragraph={{ rows: 4 }} title={false} />
              </div>
            </DashboardCard>
            <DashboardCard title="" variant="store">
              <div className="p-5">
                <Skeleton active paragraph={{ rows: 4 }} title={false} />
              </div>
            </DashboardCard>
            <DashboardCard title="" variant="store">
              <div className="p-5">
                <Skeleton active paragraph={{ rows: 4 }} title={false} />
              </div>
            </DashboardCard>
          </>
        ) : (
          sortedStoreCards.map((item) => {
            return (
              <TrackStoreStoreCard
                key={item.storeCode}
                card={item}
                onDuty={onDutyMap[item.storeCode]}
                liveStore={liveStoresMap[item.storeCode]}
                pinned={pinnedStoreCodes.includes(item.storeCode)}
                onTogglePin={togglePin}
              />
            );
          })
        )}
      </div>
    </div>
  );
};
